// Add Horse — onboard a horse in under a minute.
// Single scrolling form with card-style sections, live avatar preview at the top.

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AVATAR_TONES } from "../lib/avatarTones";
import type { HorseColour } from "../lib/avatarTones";
import { useIsMobile } from "../lib/useMediaQuery";
import { useCurrentUser } from "../lib/useCurrentUser";
import { useUserYard } from "../lib/yard";
import { createHorse, deleteHorse, updateHorse } from "../lib/horses";
import { useHorse } from "../lib/dailyLogs";
import { MOCK_YARD } from "../lib/mockData";
import "../styles/dashboard.css";
import "../styles/add-horse.css";

const AHIc = {
  back: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  id: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="3" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" /><path d="M5 6h4M5 8.5h2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  feed: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C9 4 11 5 11 8a4 4 0 0 1-8 0c0-3 2-4 4-6.5z" fill="currentColor" opacity="0.35" /><path d="M7 12V8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  notes: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 11.5L11.5 2.5L13 4L4 13L2 13L2.5 11.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>,
  chev: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M4 5L7 8L10 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  alert: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" /><path d="M7 4.5V7.5M7 9.5V9.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 4h8M5.5 4V3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1M4 4l.5 7a1 1 0 0 0 1 .9h3a1 1 0 0 0 1-.9L10 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  plus: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>,
  spark: <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M7 2L8 6L12 7L8 8L7 12L6 8L2 7L6 6L7 2z" fill="currentColor" /></svg>,
};

const BREEDS = [
  "Thoroughbred", "Irish Sport Horse", "Irish Draught", "Hanoverian",
  "Warmblood", "Connemara", "Welsh Cob", "Welsh Section D",
  "Cob", "Cob X", "New Forest", "Friesian X", "Other",
];

const COLOURS: { k: HorseColour; label: string }[] = [
  { k: "bay", label: "Bay" },
  { k: "chestnut", label: "Chestnut" },
  { k: "grey", label: "Grey" },
  { k: "black", label: "Black" },
  { k: "palomino", label: "Palomino" },
  { k: "piebald", label: "Piebald" },
  { k: "dun", label: "Dun" },
  { k: "roan", label: "Roan" },
];

const FEED_TYPES = ["Hay", "Haylage", "Grass", "Mix", "Hard feed"];

interface FeedRow {
  type: string;
  kg: number;
}

interface FormState {
  name: string;
  weightKg: string;
  breed: string;
  age: string;
  colour: HorseColour | "";
  feed: FeedRow[];
  notes: string;
}

const BLANK_FORM: FormState = {
  name: "",
  weightKg: "",
  breed: "",
  age: "",
  colour: "",
  feed: [{ type: "Hay", kg: 6 }],
  notes: "",
};

function AHTopBar({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="topbar adh-topbar">
      <div className="topbar-inner">
        <button className="icon-btn" aria-label="Back" onClick={onBack}>
          {AHIc.back}
        </button>
        <span className="adh-tb-title">{title}</span>
        <span className="muted" style={{ fontSize: 12 }}>{MOCK_YARD.name}</span>
      </div>
    </div>
  );
}

interface AHStepperProps {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

function AHStepper({ value, onChange, min = 0, max = 40, step = 0.5, unit = "kg" }: AHStepperProps) {
  const change = (delta: number) => {
    const v = Math.max(min, Math.min(max, +(value + delta).toFixed(1)));
    onChange(v);
  };
  return (
    <div className="stepper">
      <button className="stepper-btn" onClick={() => change(-step)} aria-label="Decrease" type="button">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      <div className="stepper-display">
        <span className="stepper-value mono">{value}</span>
        <span className="stepper-unit">{unit}</span>
      </div>
      <button className="stepper-btn" onClick={() => change(step)} aria-label="Increase" type="button">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

interface AHSectionProps {
  num: number;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function AHSection({ num, title, icon, children }: AHSectionProps) {
  return (
    <section className="adh-sec">
      <header className="adh-sec-head">
        <span className="adh-sec-num mono">{String(num).padStart(2, "0")}</span>
        <span className="adh-sec-icon" aria-hidden="true">{icon}</span>
        <span className="adh-sec-title">{title}</span>
      </header>
      <div className="adh-sec-body">{children}</div>
    </section>
  );
}

interface AvatarPreviewProps {
  name: string;
  colour: HorseColour | "";
}

function AvatarPreview({ name, colour }: AvatarPreviewProps) {
  const letter = name.trim()[0]?.toUpperCase() || "?";
  const tone = colour ? AVATAR_TONES[colour] : null;
  const empty = !name.trim() && !colour;
  return (
    <div
      className={`adh-preview ${empty ? "empty" : ""}`}
      style={tone ? { background: tone } : undefined}
      aria-label="Avatar preview"
    >
      {name.trim() ? letter : (
        <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M2.5 12c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

export default function AddHorse() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useCurrentUser();
  const { yard } = useUserYard(user?.uid);
  const { horseId } = useParams<{ horseId: string }>();
  const isEdit = !!horseId;
  const { horse: existingHorse, loading: existingHorseLoading } = useHorse(yard?.id, horseId);

  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(!isEdit);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onDelete = async () => {
    if (!yard?.id || !horseId) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await deleteHorse(yard.id, horseId);
      navigate("/dashboard");
    } catch (err) {
      console.error("deleteHorse failed:", err);
      setDeleteError("Couldn't delete the horse. Check your connection and try again.");
      setDeleting(false);
    }
  };

  // Hydrate the form from the existing horse once it arrives in edit mode.
  useEffect(() => {
    if (!isEdit || hydrated || !existingHorse) return;
    setForm({
      name: existingHorse.name,
      weightKg: String(existingHorse.weightKg ?? ""),
      breed: existingHorse.breed ?? "",
      age: existingHorse.age != null ? String(existingHorse.age) : "",
      colour: (existingHorse.colour && (existingHorse.colour in AVATAR_TONES)
        ? (existingHorse.colour as HorseColour)
        : "") as HorseColour | "",
      feed: existingHorse.baselineFeed?.length
        ? existingHorse.baselineFeed.map((f) => ({ type: f.type, kg: f.kg }))
        : [{ type: "Hay", kg: 6 }],
      notes: existingHorse.ownerNotes ?? "",
    });
    setHydrated(true);
  }, [isEdit, hydrated, existingHorse]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const errors = useMemo<Record<string, string>>(() => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e["name"] = "Name is required";
    const w = +form.weightKg;
    if (form.weightKg === "" || isNaN(w) || w <= 0) {
      e["weightKg"] = "Weight is required. It drives the EHE calc.";
    } else if (w < 100 || w > 1200) {
      e["weightKg"] = "Weight should be 100–1200 kg";
    }
    return e;
  }, [form]);

  const showErr = (k: string): string | null => (showErrors ? errors[k] ?? null : null);
  const canSubmit = Object.keys(errors).length === 0;

  const previewName = form.name.trim();
  const verb = isEdit ? "Editing" : "Adding";
  const headlineName = previewName
    ? `${verb} ${previewName}`
    : isEdit
      ? "Edit horse"
      : "Add a horse";
  const subline = previewName && form.weightKg
    ? `${form.weightKg}kg · ${form.breed || "Breed TBC"}`
    : isEdit
      ? "Change anything below. Saves to this horse's profile."
      : "Takes under a minute. You can edit anything later.";
  const ctaLabel = isEdit ? "Save changes" : "Add horse";
  const topBarTitle = isEdit ? "Edit horse" : "Add a horse";
  const onBack = () => navigate(isEdit && horseId ? `/horses/${horseId}` : "/dashboard");

  if (isEdit && !hydrated && existingHorseLoading) {
    return (
      <div className={`adh ${isMobile ? "is-mobile" : "is-desktop"}`}>
        <AHTopBar title={topBarTitle} onBack={onBack} />
        <main className="adh-page">
          <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}>Loading…</div>
        </main>
      </div>
    );
  }

  if (isEdit && !existingHorseLoading && !existingHorse) {
    return (
      <div className={`adh ${isMobile ? "is-mobile" : "is-desktop"}`}>
        <AHTopBar title={topBarTitle} onBack={onBack} />
        <main className="adh-page">
          <div style={{ padding: 40, textAlign: "center" }}>
            <h2 style={{ fontSize: 18, marginBottom: 6 }}>Horse not found</h2>
            <p style={{ color: "var(--ink-3)", fontSize: 14 }}>This horse isn't on your yard.</p>
            <button className="btn ghost" style={{ marginTop: 14 }} onClick={() => navigate("/dashboard")}>
              ← Back to dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  const setFeed = (i: number, patch: Partial<FeedRow>) => {
    const next = [...form.feed];
    next[i] = { ...next[i]!, ...patch };
    update("feed", next);
  };
  const addFeed = () => {
    if (form.feed.length >= 3) return;
    const used = new Set(form.feed.map((f) => f.type));
    const next = FEED_TYPES.find((t) => !used.has(t)) || "Hay";
    update("feed", [...form.feed, { type: next, kg: 2 }]);
  };
  const removeFeed = (i: number) => {
    if (form.feed.length <= 1) return;
    update("feed", form.feed.filter((_, idx) => idx !== i));
  };

  const submit = async () => {
    if (!canSubmit) {
      setShowErrors(true);
      return;
    }
    if (!user?.uid || !yard?.id) {
      setSaveError("You need to be signed in with a yard to add a horse.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      name: form.name.trim(),
      colour: form.colour || "bay",
      breed: form.breed || undefined,
      age: form.age ? +form.age : undefined,
      weightKg: +form.weightKg,
      baselineFeed: form.feed,
      ownerNotes: form.notes.trim() || null,
    };
    try {
      if (isEdit && horseId) {
        await updateHorse(yard.id, horseId, payload);
        navigate(`/horses/${horseId}`);
      } else {
        await createHorse(yard.id, user.uid, payload);
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(`${isEdit ? "updateHorse" : "createHorse"} failed:`, err);
      setSaveError("Couldn't save the horse. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`adh ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <AHTopBar title={topBarTitle} onBack={onBack} />
      <main className="adh-page">
        <div className="adh-shell">

          <header className="adh-head">
            <div className="adh-head-left">
              <AvatarPreview name={form.name} colour={form.colour} />
              <div>
                <h1 className="adh-h1">{headlineName}</h1>
                <p className="adh-h-sub">{subline}</p>
              </div>
            </div>
            {!isMobile && (
              <button
                className="btn primary adh-cta-top"
                disabled={!canSubmit || saving}
                aria-disabled={!canSubmit || saving}
                type="button"
                onClick={submit}
              >
                {saving ? "Saving…" : <>{ctaLabel} →</>}
              </button>
            )}
          </header>

          <AHSection num={1} title="Basics" icon={AHIc.id}>
            <div className="adh-field">
              <label className="adh-label" htmlFor="adh-name">
                Name <span className="adh-required" aria-hidden="true">*</span>
              </label>
              <input
                id="adh-name"
                className={`adh-input ${showErr("name") ? "has-error" : ""}`}
                placeholder="e.g. Bella"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                maxLength={40}
              />
              {showErr("name") && <span className="adh-error">{AHIc.alert} {errors["name"]}</span>}
            </div>

            <div className="adh-row">
              <div className="adh-field">
                <label className="adh-label" htmlFor="adh-weight">
                  Weight <span className="adh-required" aria-hidden="true">*</span>
                </label>
                <div className="adh-suffixed">
                  <input
                    id="adh-weight"
                    type="text"
                    inputMode="numeric"
                    className={`adh-input adh-input-numeric ${showErr("weightKg") ? "has-error" : ""}`}
                    placeholder="550"
                    value={form.weightKg}
                    onChange={(e) => update("weightKg", e.target.value.replace(/[^0-9.]/g, ""))}
                  />
                  <span className="adh-suffix">kg</span>
                </div>
                {showErr("weightKg") ? (
                  <span className="adh-error">{AHIc.alert} {errors["weightKg"]}</span>
                ) : (
                  <span className="adh-help adh-help-emph">Drives the EHE hydration target.</span>
                )}
              </div>

              <div className="adh-field">
                <label className="adh-label" htmlFor="adh-age">
                  Age <span className="adh-optional">optional</span>
                </label>
                <div className="adh-suffixed">
                  <input
                    id="adh-age"
                    type="text"
                    inputMode="numeric"
                    className="adh-input adh-input-numeric"
                    placeholder="e.g. 10"
                    value={form.age}
                    onChange={(e) => update("age", e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  <span className="adh-suffix">yrs</span>
                </div>
              </div>
            </div>

            <div className="adh-field">
              <label className="adh-label" htmlFor="adh-breed">
                Breed <span className="adh-optional">optional</span>
              </label>
              <div className="adh-select-wrap">
                <select
                  id="adh-breed"
                  className="adh-select"
                  value={form.breed}
                  data-placeholder={!form.breed}
                  onChange={(e) => update("breed", e.target.value)}
                >
                  <option value="">Pick a breed…</option>
                  {BREEDS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
                <span className="adh-select-chev" aria-hidden="true">{AHIc.chev}</span>
              </div>
            </div>

            <div className="adh-field">
              <label className="adh-label">
                Colour <span className="adh-optional">optional · tints the avatar</span>
              </label>
              <div className="adh-colours" role="radiogroup" aria-label="Colour">
                {COLOURS.map((c) => (
                  <button
                    key={c.k}
                    role="radio"
                    aria-checked={form.colour === c.k}
                    className={`adh-colour ${form.colour === c.k ? "active" : ""}`}
                    onClick={() => update("colour", form.colour === c.k ? "" : c.k)}
                    type="button"
                  >
                    <span
                      className="adh-colour-swatch"
                      style={{ background: AVATAR_TONES[c.k] }}
                    ></span>
                    <span className="adh-colour-label">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </AHSection>

          <AHSection num={2} title="Baseline feed" icon={AHIc.feed}>
            <p className="adh-help">
              Used as defaults when you log daily. Overridable each day. Up to 3 entries.
            </p>
            <div className="adh-feed-list">
              {form.feed.map((f, i) => (
                <div key={i} className="adh-feed-row">
                  <div className="adh-feed-type-field adh-field">
                    <label className="adh-label">Feed type</label>
                    <div className="adh-select-wrap">
                      <select
                        className="adh-select"
                        value={f.type}
                        onChange={(e) => setFeed(i, { type: e.target.value })}
                      >
                        {FEED_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <span className="adh-select-chev" aria-hidden="true">{AHIc.chev}</span>
                    </div>
                  </div>
                  <div className="adh-feed-amount-field adh-field">
                    <label className="adh-label">Amount / day</label>
                    <AHStepper
                      value={f.kg}
                      onChange={(v) => setFeed(i, { kg: v })}
                      step={0.5}
                      max={40}
                    />
                  </div>
                  <button
                    type="button"
                    className={`adh-feed-remove ${form.feed.length <= 1 ? "is-hidden" : ""}`}
                    onClick={() => removeFeed(i)}
                    aria-label={`Remove ${f.type}`}
                  >
                    {AHIc.trash}
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="adh-add-feed"
              onClick={addFeed}
              disabled={form.feed.length >= 3}
            >
              {AHIc.plus} {form.feed.length >= 3 ? "Maximum 3 feed types" : "Add another feed type"}
            </button>
          </AHSection>

          <AHSection num={3} title="Owner notes" icon={AHIc.notes}>
            <p className="adh-help">
              Anything we should know? Quirks, allergies, vet history, who owns this horse.
            </p>
            <textarea
              className="adh-textarea"
              placeholder="e.g. Tendency to leave water if trough is by the gate. Box-rests after Saturday hacks."
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              maxLength={400}
            />
          </AHSection>

          {previewName && form.weightKg && !errors["weightKg"] && (
            <div className="adh-summary">
              <span className="adh-summary-icon">{AHIc.spark}</span>
              <span>
                <strong>{previewName}</strong> · estimated daily water target{" "}
                <span className="mono">
                  {Math.round(+form.weightKg * 0.055 + (MOCK_YARD.weatherC > 18 ? 6 : 3))}L
                </span>{" "}
                at today's {MOCK_YARD.weatherC}°C
              </span>
            </div>
          )}

          {saveError && (
            <div
              role="alert"
              style={{
                background: "var(--status-severe-soft)",
                color: "var(--status-severe)",
                border: "1px solid var(--status-severe-soft)",
                borderRadius: "var(--r-md)",
                padding: "12px 14px",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {AHIc.alert} {saveError}
            </div>
          )}

          <div className="adh-foot">
            <p className="adh-foot-note">
              {isEdit
                ? `Saves to ${previewName || "this horse"}'s profile.`
                : `You'll be redirected to ${previewName || "the horse"}'s profile after adding.`}
            </p>
            <button
              type="button"
              className="btn primary adh-foot-cta"
              disabled={!canSubmit || saving}
              onClick={submit}
            >
              {saving ? "Saving…" : <>{ctaLabel} →</>}
            </button>
          </div>

          {isEdit && (
            <div
              style={{
                marginTop: 32,
                padding: "20px 22px",
                border: "1px solid var(--status-severe-soft)",
                borderRadius: "var(--r-md)",
                background: "var(--status-severe-soft)",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--status-severe)", marginBottom: 4 }}>
                Delete this horse
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
                Permanently removes {previewName || "this horse"} and every daily log on file. Cannot be undone.
              </p>
              {!confirmingDelete ? (
                <button
                  type="button"
                  className="btn ghost"
                  style={{ color: "var(--status-severe)", borderColor: "var(--status-severe)" }}
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete horse…
                </button>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    style={{ background: "var(--status-severe)", borderColor: "var(--status-severe)" }}
                    onClick={onDelete}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting…" : `Yes, delete ${previewName || "this horse"}`}
                  </button>
                </div>
              )}
              {deleteError && (
                <p style={{ fontSize: 12, color: "var(--status-severe)", margin: "10px 0 0" }}>
                  {deleteError}
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      {isMobile && (
        <div className="adh-cta-wrap">
          <button type="button" className="btn primary adh-cta" disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Saving…" : <>{ctaLabel} →</>}
          </button>
        </div>
      )}
    </div>
  );
}
